/**
 * Sticky Add to Cart — mirrors the main product form (no second cart/submit path).
 * Shows when the primary Add to Cart button leaves the viewport.
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.bar = this.querySelector('[data-sticky-atc]');
        this.variantEl = this.querySelector('[data-sticky-variant]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.button = this.querySelector('[data-sticky-submit]');
        this.buttonLabel = this.button?.querySelector('[data-sticky-label]');

        this.mainButton =
          document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
          document.querySelector(`#product-form-${this.sectionId} [type="submit"]`);
        this.productForm = document.querySelector(`#product-form-${this.sectionId}`)?.closest('product-form');
        this.form = document.getElementById(`product-form-${this.sectionId}`);
        this.variantInput = this.form?.querySelector('input[name="id"]');
        this.variantRoot = document.getElementById(`variant-selects-${this.sectionId}`);

        if (!this.bar || !this.mainButton || !this.form) return;

        this.onStickyClick = this.onStickyClick.bind(this);
        this.syncFromMain = this.syncFromMain.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        this.button?.addEventListener('click', this.onStickyClick);
        this.observeMainButton();
        this.bindVariantChanges();
        this.syncFromMain();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.button?.removeEventListener('click', this.onStickyClick);
        this.variantUnsub?.();
      }

      observeMainButton() {
        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            const shouldShow = entry && !entry.isIntersecting;
            this.bar.classList.toggle('is-visible', Boolean(shouldShow));
            this.bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
            document.documentElement.classList.toggle('sticky-atc-active', Boolean(shouldShow));
          },
          {
            root: null,
            threshold: 0,
            rootMargin: '0px 0px 0px 0px',
          }
        );
        this.observer.observe(this.mainButton);
      }

      bindVariantChanges() {
        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
          this.variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }
        // Quantity / availability may update without pubsub in some paths
        this.mainButtonObserver = new MutationObserver(() => this.syncFromMain());
        this.mainButtonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
        });
        if (this.variantInput) {
          this.variantInput.addEventListener('change', this.syncFromMain);
        }
      }

      onVariantChange(event) {
        const data = event?.data;
        if (data?.sectionId && String(data.sectionId) !== String(this.sectionId)) return;
        // Re-query main button after section HTML morphs
        this.mainButton =
          document.getElementById(`ProductSubmitButton-${this.sectionId}`) || this.mainButton;
        this.productForm =
          document.querySelector(`#product-form-${this.sectionId}`)?.closest('product-form') ||
          this.productForm;
        this.form = document.getElementById(`product-form-${this.sectionId}`) || this.form;
        this.variantInput = this.form?.querySelector('input[name="id"]');
        this.syncFromMain(data?.variant);
      }

      syncFromMain(variant) {
        const priceSource = document.getElementById(`price-${this.sectionId}`);
        if (this.priceEl && priceSource) {
          // Copy price without sale badges / sold-out chips cluttering the sticky bar
          const clone = priceSource.cloneNode(true);
          clone
            .querySelectorAll(
              '.badge, .price__badge-sale, .price__badge-sold-out, .visually-hidden, .visually-hidden--inline'
            )
            .forEach((el) => el.remove());
          this.priceEl.innerHTML = clone.innerHTML;
        }

        const variantLabel = this.resolveVariantLabel(variant);
        if (this.variantEl) {
          this.variantEl.textContent = variantLabel;
          this.variantEl.hidden = !variantLabel;
        }

        const unavailable =
          this.mainButton?.hasAttribute('disabled') ||
          this.mainButton?.getAttribute('aria-disabled') === 'true' ||
          this.variantInput?.disabled;

        const soldOutText =
          this.mainButton?.querySelector('span:not(.sold-out-message):not(.loading__spinner)')
            ?.textContent?.trim() ||
          this.mainButton?.innerText?.trim() ||
          '';

        if (this.button) {
          if (unavailable) {
            this.button.setAttribute('disabled', 'disabled');
            this.button.setAttribute('aria-disabled', 'true');
          } else {
            this.button.removeAttribute('disabled');
            this.button.removeAttribute('aria-disabled');
          }
        }

        if (this.buttonLabel) {
          const label =
            unavailable && /sold/i.test(soldOutText)
              ? soldOutText
              : unavailable && /unavailable/i.test(soldOutText)
                ? soldOutText
                : this.dataset.addToCartLabel || 'Add to cart';
          this.buttonLabel.textContent = label;
        }
      }

      resolveVariantLabel(variant) {
        if (variant && variant.title && variant.title !== 'Default Title') {
          return variant.title;
        }

        // Re-query after section HTML updates from variantChange
        this.variantRoot =
          document.getElementById(`variant-selects-${this.sectionId}`) || this.variantRoot;

        const checked = this.variantRoot
          ? [...this.variantRoot.querySelectorAll('input[type="radio"]:checked, select option:checked')]
              .map((el) => (el.tagName === 'OPTION' ? el.textContent : el.value || el.getAttribute('value') || ''))
              .map((v) => v.trim())
              .filter((v) => v && v !== 'Default Title')
          : [];
        if (checked.length) return checked.join(' / ');

        const selected = this.variantRoot
          ? [...this.variantRoot.querySelectorAll('[data-selected-value]')]
              .map((el) => el.textContent.trim())
              .filter(Boolean)
          : [];
        if (selected.length) return selected.join(' / ');

        const jsonScript = this.variantRoot?.querySelector('[data-selected-variant]');
        if (jsonScript) {
          try {
            const parsed = JSON.parse(jsonScript.textContent);
            if (parsed?.title && parsed.title !== 'Default Title') return parsed.title;
          } catch (e) {
            /* ignore */
          }
        }

        return this.dataset.productTitle || '';
      }

      needsVariantSelection() {
        if (!this.variantRoot) return false;
        const id = this.variantInput?.value;
        if (!id) return true;
        // Dawn can leave options unselected for picker UIs
        const uncheckedRequired = this.variantRoot.querySelector(
          'input[type="radio"]:required:not(:checked)'
        );
        if (uncheckedRequired) return true;
        // Size search picker empty
        const sizeFilter = this.variantRoot.querySelector('[data-size-filter]');
        if (sizeFilter && !sizeFilter.value.trim()) return true;
        return false;
      }

      onStickyClick(event) {
        event.preventDefault();
        if (this.button?.hasAttribute('disabled') || this.button?.getAttribute('aria-disabled') === 'true') {
          return;
        }

        if (this.needsVariantSelection()) {
          this.focusVariantPicker();
          return;
        }

        // Delegate to the real product-form submit path (single cart add / events)
        if (this.mainButton && !this.mainButton.hasAttribute('disabled')) {
          this.mainButton.click();
          return;
        }

        if (typeof this.form?.requestSubmit === 'function') {
          this.form.requestSubmit(this.mainButton || undefined);
        } else {
          this.form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }

      focusVariantPicker() {
        const target =
          this.variantRoot ||
          document.getElementById(`variant-selects-${this.sectionId}`) ||
          document.querySelector(`#ProductInfo-${this.sectionId} fieldset, #ProductInfo-${this.sectionId} .product-form__input`);

        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable =
          target.querySelector('input:not([type="hidden"]), select, button, [tabindex]') || target;
        try {
          focusable.focus({ preventScroll: true });
        } catch (e) {
          /* ignore */
        }
        target.classList.add('sticky-atc__highlight');
        setTimeout(() => target.classList.remove('sticky-atc__highlight'), 1600);
      }
    }
  );
}
